import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { useToast } from '../../ui/index.js';

/**
 * One mutation hook for every configuration write.
 *
 * All of them do the same three things afterwards — invalidate the tenant
 * config (the SPA reads queues, types, fields and SLA from it everywhere),
 * invalidate the admin lists, and say so — so they share one path rather than
 * seven near-identical `useMutation` calls that drift.
 *
 * The server's message is surfaced verbatim on failure. The refusals are the
 * interesting part of this screen ("3 open tickets are still in this queue"),
 * and replacing them with "Save failed" would throw away the only useful
 * information the user gets.
 */
export interface ConfigWrite {
  method: 'post' | 'patch' | 'put' | 'del';
  path: string;
  body?: unknown;
  /** What to say when it lands. */
  done: string;
}

/**
 * Query keys a configuration write can invalidate. Exported so a test can
 * prove no admin list is missing from it: an admin list whose key is absent
 * here keeps showing pre-write data until the page is remounted, which reads
 * as "the save did nothing". That happened to the webhooks list.
 */
export const TOUCHED = [
  'config',
  'admin-fields',
  'admin-rules',
  'admin-roles',
  'admin-history',
  'admin-webhooks',
  'admin-knowledge',
  'admin-modules',
  'admin-actions',
  'admin-keys',
  'admin-outbound',
  'admin-intake',
  'admin-intake-suggestions',
  'admin-mail-intake',
  'admin-role-capabilities',
];

export function useConfigWrite(tenant: string) {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (w: ConfigWrite): Promise<unknown> => {
      if (w.method === 'del') return api.del(w.path);
      if (w.method === 'put') return api.put(w.path, w.body);
      if (w.method === 'patch') return api.patch(w.path, w.body);
      return api.post(w.path, w.body);
    },
    onSuccess: (_data, w) => {
      for (const key of TOUCHED) void qc.invalidateQueries({ queryKey: [key, tenant] });
      toast.success(w.done);
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : 'The change could not be saved';
      const fields = err instanceof ApiError ? err.fields : undefined;
      /**
       * Only when it ADDS something. With no top-level `message` the client
       * builds the title by joining the field messages, so rendering them
       * again underneath printed the same sentence twice — which reads as a
       * bug in the product rather than an explanation of one.
       */
      const detail =
        fields && fields.length > 0
          ? fields.map((f) => `${f.key}: ${f.message}`).join(' · ')
          : undefined;
      const sameThingTwice =
        detail !== undefined && fields!.every((f) => msg.includes(f.message));
      toast.error(msg, sameThingTwice ? undefined : detail);
    },
  });
}
